import type { FloorplanDoor, FloorplanRoom } from "./types";

/*
 * Marks drawn on a plan that take no clicks. The viewer draws them, and so does a static cut-out of a plan (the
 * member portal's directions, one small map per step), so both show the same marks.
 */

/** Each room's area ("~24 m²") in its polygon's bottom-right corner. */
export function FloorplanAreaLabels({ rooms }: { rooms: FloorplanRoom[] }) {
  return (
    <>
      {rooms.map((room) => {
        if (!room.squareMeters || !room.polygon?.[0]?.length) return null;
        const poly = room.polygon[0];
        const maxX = Math.max(...poly.map((p) => p[0]));
        const maxY = Math.max(...poly.map((p) => p[1]));
        return (
          <text
            key={`m2-${room.id}`}
            x={maxX - 4}
            y={maxY - 4}
            textAnchor="end"
            fontSize={9}
            fill="#94a3b8"
            opacity={0.8}
            style={{ pointerEvents: "none" }}
          >
            ~{Math.floor(room.squareMeters!)} m²
          </text>
        );
      })}
    </>
  );
}

/** Doors: a Point door's icon (🚪, or 🚨 for an emergency exit) and an arrow head off each enabled side. */
export function FloorplanDoorMarkers({ doors }: { doors?: FloorplanDoor[] }) {
  return (
    <>
      {(doors ?? []).map((d) => {
        const g = d.geometry;
        if (!g || g.type !== "Point") return null;
        const [cx, cy] = g.coordinates;
        if (cx == null || cy == null) return null;
        const isEmergencyExit = d.doorType === "emergency_exit";
        const color = isEmergencyExit ? "#ef4444" : "#a855f7";
        const sides = d.arrowSides ?? {};
        const arrowLen = 18;
        const arrowOffset = 11;
        const arrowHead = 4;
        const drawArrow = (dir: "l" | "r" | "t" | "b") => {
          const dx = dir === "r" ? 1 : dir === "l" ? -1 : 0;
          const dy = dir === "b" ? 1 : dir === "t" ? -1 : 0;
          const x1 = cx + dx * arrowOffset;
          const y1 = cy + dy * arrowOffset;
          const x2 = x1 + dx * arrowLen;
          const y2 = y1 + dy * arrowLen;
          const px = -dy * arrowHead;
          const py = dx * arrowHead;
          return (
            <g key={`door-${d.id}-${dir}`} stroke={color} strokeWidth={2} fill="none">
              <line x1={x1} y1={y1} x2={x2} y2={y2} />
              <line x1={x2} y1={y2} x2={x2 - dx * arrowHead + px} y2={y2 - dy * arrowHead + py} />
              <line x1={x2} y1={y2} x2={x2 - dx * arrowHead - px} y2={y2 - dy * arrowHead - py} />
            </g>
          );
        };
        return (
          <g key={`door-${d.id}`} style={{ pointerEvents: "none" }}>
            <circle cx={cx} cy={cy} r={8} fill="white" stroke={color} strokeWidth={1.8} />
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={9}>
              {isEmergencyExit ? "🚨" : "🚪"}
            </text>
            {sides.l && drawArrow("l")}
            {sides.r && drawArrow("r")}
            {sides.t && drawArrow("t")}
            {sides.b && drawArrow("b")}
          </g>
        );
      })}
    </>
  );
}
